import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { operationsApi } from '@/services/api';
import type { OperationStep } from '@/types/operations';
import { formatDateTime } from '@/utils/formatters';
import { CheckTypeBadge, StepStatusBadge, formatCategoryLabel } from './presentation';
import { renderStepDetail } from './stepDetailRenderers';
import { getApiErrorMessage } from './helpers';
import { PageFrame, PageHeader, Surface, Note, Placeholder, Button } from '@/components/ui';
import { DetailSection, DetailField } from '@/components/utils/PageCard';
import styles from './OperationsChecklist.module.css';

// Read-only view of a single step's full detail (result_detail rendered, exemption info).
// Actions (Re-run, Confirm, Exempt) remain on the checklist list page.
const StepDetailPage: React.FC = () => {
  const { stepId } = useParams<{ stepId: string }>();
  const numStepId = Number(stepId ?? '');
  const navigate = useNavigate();
  const [step, setStep] = useState<OperationStep | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(numStepId)) {
      setError('Invalid step ID.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await operationsApi.getStep(numStepId);
      setStep(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load step details.'));
    } finally {
      setLoading(false);
    }
  }, [numStepId]);

  useEffect(() => {
    void load();
  }, [load]);

  const backAction = (
    <Button onClick={() => navigate(-1)} icon={<ArrowLeft size={16} />}>
      Back
    </Button>
  );

  if (loading) {
    return (
      <PageFrame header={<PageHeader title="Step" actions={backAction} />}>
        <Surface>
          <Placeholder kind="loading" message="Loading step…" />
        </Surface>
      </PageFrame>
    );
  }

  if (error || !step) {
    return (
      <PageFrame header={<PageHeader title="Step" actions={backAction} />}>
        <Note tone="error">{error || 'Step not found.'}</Note>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      header={
        <PageHeader
          title={step.title}
          subtitle={
            <span className={styles['detail-badges']}>
              <StepStatusBadge status={step.status} />
              <CheckTypeBadge checkType={step.check_type} />
            </span>
          }
          actions={backAction}
        />
      }
    >
      <Surface padded>
        <DetailSection title="Step Information">
          {step.description && (
            <div style={{ gridColumn: '1 / -1' }}>
              <DetailField label="Description" value={step.description} />
            </div>
          )}
          <DetailField label="Category" value={formatCategoryLabel(step.category)} />
          <DetailField label="Check Type" value={step.check_type === 'AUTO' ? 'Automatic' : 'Manual'} />
          <DetailField label="Last Run" value={step.last_run_ts ? formatDateTime(step.last_run_ts) : undefined} />
          {step.summary && (
            <div style={{ gridColumn: '1 / -1' }}>
              <DetailField label="Summary" value={step.summary} />
            </div>
          )}
        </DetailSection>
      </Surface>

      {step.status === 'EXEMPTED' && (
        <Surface title="Exemption" padded>
          <p className={styles['exemption-reason']}>{step.exemption_reason || '—'}</p>
          <span className={styles['exemption-meta']}>
            {step.resolved_by_email ? `By ${step.resolved_by_email}` : ''}
            {step.resolved_ts ? ` · ${formatDateTime(step.resolved_ts)}` : ''}
          </span>
        </Surface>
      )}

      <Surface title="Check Result" padded>
        <div className={styles['step-detail-content']}>
          {renderStepDetail(step)}
        </div>
      </Surface>
    </PageFrame>
  );
};

export default StepDetailPage;
